import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { AppWindow, House, ZoomIn, ZoomOut } from 'lucide-react';
import Button, { buttonClass } from './ui/Button';
import ActionRow from './ui/ActionRow';
import LabelPhotoImage, { rotationDegrees } from './LabelPhotoImage';
import { isProjectStoragePhoto, labelPhotoWindowUrl } from '../utils/labelPhotoWindow';

/**
 * FS-1079 — a label photo to read: turned by its stored rotation, zoomed from
 * the whole photo up to four times its own pixels, and moved around by
 * scrolling or dragging. The console's large view and the photo's own window
 * both draw it through this one component.
 *
 * The photo is laid out at its zoomed size inside a scroll area, so the
 * browser's own scrolling (scrollbars, trackpad, wheel) moves it and stops at
 * its edges. While it is smaller than the area, margins center it.
 */

/** One Zoom in or Zoom out press. */
const ZOOM_STEP = 1.25;
/** The furthest zoom, in times the photo's own pixels: enough to read a label's small print. */
const MAX_ZOOM = 4;
/** A mouse wheel notch, in pixels of `deltaY`: one notch zooms one step. */
const WHEEL_NOTCH = 100;
/** A wheel line, in pixels, for a wheel that reports lines. */
const WHEEL_LINE = 16;
/** Zooms this close count as the same, so rounding never leaves Zoom out on at fit. */
const SAME_ZOOM = 1e-6;
/** The pop-out window's opening size. */
const WINDOW_FEATURES = 'popup,width=1200,height=900';

const AT_FIT_REASON = 'The whole photo is in view, so there is nothing to zoom out to or go home to.';
const AT_MAX_REASON = `Zoomed in as far as it goes: ${MAX_ZOOM} times the photo's own size.`;
const NOT_STORED_REASON = "This photo is not in the project's photo storage, so it cannot open in its own window.";
const POP_OUT_BLOCKED = 'The browser blocked the new window. Allow pop-ups for this console, or open the file in a new tab.';

interface Size {
  width: number;
  height: number;
}

/** A point in the scroll area, and where it falls on the photo as fractions of its size. */
interface Anchor {
  x: number;
  y: number;
  fractionX: number;
  fractionY: number;
}

interface LabelPhotoZoomProps {
  src: string;
  alt: string;
  degrees: number | null | undefined;
  /** Hover help for the link to the file as stored. */
  fileTitle: string;
  /** The Rotate the opening place offers; absent where there is none. */
  rotate?: React.ReactNode;
  /** Offer Pop out: in the console's large view, not in the window it opens. */
  popOut?: boolean;
}

/** The photo's own size, turned. */
function turnedSize(natural: Size, degrees: number): Size {
  const quarter = degrees === 90 || degrees === 270;
  return quarter ? { width: natural.height, height: natural.width } : natural;
}

/** The margin that centers a length inside a longer one, or none. */
const centering = (outer: number, inner: number): number => Math.max(0, (outer - inner) / 2);

const LabelPhotoZoom: React.FC<LabelPhotoZoomProps> = ({ src, alt, degrees, fileTitle, rotate, popOut = false }) => {
  const turn = rotationDegrees(degrees);
  const area = useRef<HTMLDivElement>(null);
  const reasonId = useId();
  const [natural, setNatural] = useState<Size | null>(null);
  const [areaSize, setAreaSize] = useState<Size | null>(null);
  /** The chosen zoom in times the photo's own pixels, or null for the whole photo (Home). */
  const [chosen, setChosen] = useState<number | null>(null);
  const [popOutBlocked, setPopOutBlocked] = useState(false);
  const [drag, setDrag] = useState<{ pointer: number; x: number; y: number; left: number; top: number } | null>(null);
  // A new turn or photo starts at Home.
  const [shown, setShown] = useState({ src, turn });
  if (shown.src !== src || shown.turn !== turn) {
    setShown({ src, turn });
    setChosen(null);
  }
  /** Where the next render puts the zoom's fixed point back under the pointer or center. */
  const anchor = useRef<Anchor | null>(null);

  useLayoutEffect(() => {
    const element = area.current;
    if (!element) return undefined;
    const measure = () => setAreaSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const photo = natural ? turnedSize(natural, turn) : null;
  const fit = photo && areaSize && photo.width > 0 && photo.height > 0
    ? Math.min(areaSize.width / photo.width, areaSize.height / photo.height)
    : null;
  const max = fit === null ? null : Math.max(fit, MAX_ZOOM);
  const zoom = fit === null || max === null ? null : Math.min(max, Math.max(fit, chosen ?? fit));
  const atFit = zoom === null || fit === null || zoom <= fit * (1 + SAME_ZOOM);
  const atMax = zoom === null || max === null || zoom >= max * (1 - SAME_ZOOM);
  const drawn = photo && zoom !== null ? { width: photo.width * zoom, height: photo.height * zoom } : null;
  const scrollable = Boolean(drawn && areaSize && (drawn.width > areaSize.width + 1 || drawn.height > areaSize.height + 1));

  /** Zoom to `next`, keeping the photo point at `point` (in the scroll area) where it is. */
  const zoomAt = (next: number, point: { x: number; y: number }) => {
    const element = area.current;
    if (!element || !drawn || zoom === null || fit === null || max === null) return;
    const target = Math.min(max, Math.max(fit, next));
    // Already there (at the limit): no render follows, so no fixed point is kept for one.
    if (Math.abs(target - zoom) <= zoom * SAME_ZOOM) return;
    // Several wheel events can arrive before one render: keep the first fixed
    // point, read while the layout and the scroll position still agreed.
    if (!anchor.current) {
      anchor.current = {
        x: point.x,
        y: point.y,
        fractionX: (element.scrollLeft + point.x - centering(element.clientWidth, drawn.width)) / drawn.width,
        fractionY: (element.scrollTop + point.y - centering(element.clientHeight, drawn.height)) / drawn.height,
      };
    }
    setChosen(target <= fit * (1 + SAME_ZOOM) ? null : target);
  };

  useLayoutEffect(() => {
    const element = area.current;
    const fixed = anchor.current;
    if (!element || !fixed || !drawn) return;
    anchor.current = null;
    element.scrollLeft = fixed.fractionX * drawn.width + centering(element.clientWidth, drawn.width) - fixed.x;
    element.scrollTop = fixed.fractionY * drawn.height + centering(element.clientHeight, drawn.height) - fixed.y;
  });

  const center = () => {
    const element = area.current;
    return { x: (element?.clientWidth ?? 0) / 2, y: (element?.clientHeight ?? 0) / 2 };
  };
  const zoomIn = () => { if (zoom !== null) zoomAt(zoom * ZOOM_STEP, center()); };
  const zoomOut = () => { if (zoom !== null) zoomAt(zoom / ZOOM_STEP, center()); };
  const home = () => setChosen(null);

  // The wheel listener is attached once; it reads the zoom of the latest render.
  const latest = useRef({ zoom, zoomAt });
  latest.current = { zoom, zoomAt };
  useEffect(() => {
    const element = area.current;
    if (!element) return undefined;
    const onWheel = (event: WheelEvent) => {
      // A plain wheel scrolls the photo; Ctrl or Cmd with the wheel, and a trackpad pinch, zoom it.
      if (!event.ctrlKey && !event.metaKey) return;
      // Non-passive so this stops the browser zooming the whole page for the same gesture.
      event.preventDefault();
      const { zoom: current, zoomAt: zoomTo } = latest.current;
      if (current === null) return;
      const pixels = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * WHEEL_LINE : event.deltaY;
      const box = element.getBoundingClientRect();
      zoomTo(current * ZOOM_STEP ** (-pixels / WHEEL_NOTCH), { x: event.clientX - box.left, y: event.clientY - box.top });
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, []);

  const onKeyDown = (event: React.KeyboardEvent) => {
    // Ctrl or Cmd with + and - is the browser's own page zoom; leave it alone.
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const action = event.key === '+' || event.key === '=' ? zoomIn
      : event.key === '-' ? zoomOut
        : event.key === '0' ? home
          : null;
    if (!action) return;
    event.preventDefault();
    action();
  };

  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = area.current;
    if (!element || !scrollable || event.pointerType !== 'mouse' || event.button !== 0) return;
    element.setPointerCapture(event.pointerId);
    setDrag({ pointer: event.pointerId, x: event.clientX, y: event.clientY, left: element.scrollLeft, top: element.scrollTop });
  };
  const moveDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = area.current;
    if (!element || !drag || drag.pointer !== event.pointerId) return;
    element.scrollLeft = drag.left - (event.clientX - drag.x);
    element.scrollTop = drag.top - (event.clientY - drag.y);
  };
  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (drag?.pointer === event.pointerId) setDrag(null);
  };

  const openWindow = () => {
    const opened = window.open(labelPhotoWindowUrl(src, turn), '_blank', WINDOW_FEATURES);
    setPopOutBlocked(opened === null);
  };
  const stored = isProjectStoragePhoto(src);
  const reasons = [
    atFit ? AT_FIT_REASON : null,
    atMax ? AT_MAX_REASON : null,
    popOut && !stored ? NOT_STORED_REASON : null,
  ].filter((reason): reason is string => reason !== null);
  const describedBy = (blocked: boolean) => (blocked ? reasonId : undefined);

  return (
    <div className="flex min-h-0 flex-1 flex-col" onKeyDown={onKeyDown}>
      <div
        ref={area}
        tabIndex={0}
        role="region"
        aria-label="Photo: scroll or drag to move around it; + and - zoom, 0 shows the whole photo"
        data-testid="label-photo-area"
        className={`min-h-0 flex-1 overflow-auto bg-muted ${scrollable ? (drag ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        // The browser would otherwise drag the image itself out of the page.
        onDragStart={(event) => event.preventDefault()}
      >
        <LabelPhotoImage
          src={src}
          alt={alt}
          degrees={turn}
          boxClassName=""
          fit="contain"
          onLoad={(event) => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
          // Inline: the zoomed size and the centering margins are measured, not theme values.
          boxStyle={drawn && areaSize ? {
            width: drawn.width,
            height: drawn.height,
            margin: `${centering(areaSize.height, drawn.height)}px ${centering(areaSize.width, drawn.width)}px`,
          } : { width: areaSize?.width ?? 0, height: areaSize?.height ?? 0 }}
        />
      </div>
      <ActionRow className="shrink-0 border-t p-3" reason={reasons.join(' ')} reasonId={reasonId}>
        <Button
          variant="secondary"
          size="sm"
          onClick={zoomOut}
          disabled={atFit}
          aria-describedby={describedBy(atFit)}
          aria-label="Zoom out"
          title="Zoom out on the photo (-)"
        >
          <ZoomOut className="h-4 w-4" aria-hidden="true" />
        </Button>
        <span data-testid="label-photo-zoom" className="min-w-12 text-center text-sm tabular-nums" title="Zoom, in times the photo's own pixels">
          {zoom === null ? '…' : `${Math.round(zoom * 100)}%`}
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={zoomIn}
          disabled={atMax}
          aria-describedby={describedBy(atMax)}
          aria-label="Zoom in"
          title="Zoom in on the photo (+)"
        >
          <ZoomIn className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={home}
          disabled={atFit}
          aria-describedby={describedBy(atFit)}
          aria-label="Home"
          title="Show the whole photo, centered (0)"
        >
          <House className="h-4 w-4" aria-hidden="true" />
        </Button>
        {popOut && (
          <Button
            variant="secondary"
            size="sm"
            onClick={openWindow}
            disabled={!stored}
            aria-describedby={describedBy(!stored)}
            aria-label="Pop out"
            title="Open this photo in its own window, turned the same way, with the same zoom controls"
          >
            <AppWindow className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
        {rotate && <div>{rotate}</div>}
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass('link', 'md')}
          title={fileTitle}
        >
          Open the file in a new tab
        </a>
      </ActionRow>
      {popOutBlocked && (
        <p role="status" className="shrink-0 px-3 pb-3 text-sm text-status-error-foreground">{POP_OUT_BLOCKED}</p>
      )}
    </div>
  );
};

export default LabelPhotoZoom;
